import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { HelpTip } from '../components/HelpTip';
import { Badge } from '../components/Badge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OCItem {
  id: number;
  sku?: string;
  descripcion: string;
  cantidad: string;
  cantidadPendiente: number;
  cantidadRecibida: number;
  precioUnitario?: string;
  unidadMedida?: string;
}

interface OrdenRecibible {
  id: number;
  proveedorId: number;
  proveedor: { nombre: string };
  fecha: string;
  fechaEntrega: string | null;
  estado: 'pendiente' | 'parcial';
  items: OCItem[];
  numeroOrden?: string;
  total?: string;
  moneda?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DAY_HEADERS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const estadoConfig: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  pendiente: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    badge: 'bg-amber-100 text-amber-700',
  },
  parcial: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-700',
  },
};

// ---------------------------------------------------------------------------
// Calendar helpers
// ---------------------------------------------------------------------------

function getMonthDays(year: number, month: number): (number | null)[][] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Day of week for the 1st: 0=Sun … 6=Sat → we want 0=Mon … 6=Sun
  let startDow = new Date(year, month, 1).getDay() - 1;
  if (startDow < 0) startDow = 6;

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  // Leading padding
  for (let i = 0; i < startDow; i++) {
    currentWeek.push(null);
  }

  // Fill actual days
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Trailing padding
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

function formatDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// PlannerPage
// ---------------------------------------------------------------------------

export function PlannerPage() {
  const navigate = useNavigate();
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  const { data: ordenes, loading, error, refetch } = useApi<OrdenRecibible[]>(
    '/api/v3/compras/recibibles',
  );

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  // Filter to only OCs with at least one pending item (safety net — backend already filters)
  const pendientes = useMemo(
    () =>
      (ordenes || []).filter((oc) =>
        oc.items?.some((item) => item.cantidadPendiente > 0),
      ),
    [ordenes],
  );

  // Group by fechaEntrega
  const { byDate, sinFecha } = useMemo(() => {
    const byDate: Record<string, OrdenRecibible[]> = {};
    const sinFecha: OrdenRecibible[] = [];

    for (const oc of pendientes) {
      if (!oc.fechaEntrega) {
        sinFecha.push(oc);
        continue;
      }
      // fechaEntrega is an ISO datetime string from the API — use date portion only
      const dateStr = oc.fechaEntrega.slice(0, 10);
      if (!byDate[dateStr]) byDate[dateStr] = [];
      byDate[dateStr].push(oc);
    }

    return { byDate, sinFecha };
  }, [pendientes]);

  const weeks = useMemo(() => getMonthDays(year, month), [year, month]);

  // Check if viewing today's month (for today-highlight)
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === month;
  const todayDay = today.getDate();

  // -----------------------------------------------------------------------
  // Helpers for rendering OC cards
  // -----------------------------------------------------------------------

  function renderOCCard(oc: OrdenRecibible) {
    const cfg = estadoConfig[oc.estado] || estadoConfig.pendiente;
    const itemCount = oc.items?.length ?? 0;

    return (
      <div
        key={oc.id}
        onClick={() => navigate(`/compras?id=${oc.id}`)}
        className={`mt-1 p-1.5 rounded border text-xs ${cfg.border} ${cfg.bg} cursor-pointer hover:shadow-sm transition-shadow`}
        title={`OC #${oc.id} — ${oc.proveedor.nombre}\n${itemCount} item${itemCount !== 1 ? 's' : ''} pendiente${itemCount !== 1 ? 's' : ''}`}
      >
        <div className="font-medium text-slate-700 truncate">
          {oc.proveedor.nombre}
        </div>
        <span className={cfg.text}>
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </span>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Planner</h1>
        <HelpTip
          tips={[
            'Calendario de planificación de compras y entregas',
            'Visualizá las fechas de entrega de órdenes de compra pendientes',
            'Planificá recepciones y pagos futuros',
          ]}
        />
        <p className="text-slate-500 text-sm mt-1">
          Planificación de compras y abastecimiento
          {ordenes && (
            <Badge tone="brand" className="ml-3 gap-1 border border-brand-200">
              {pendientes.length} pendiente{pendientes.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </p>
      </div>

      {/* Month navigation + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Mes anterior"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-slate-800 min-w-[180px] text-center">
            {MONTHS[month]} {year}
          </h2>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Mes siguiente"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <button
          onClick={refetch}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50 transition-colors"
        >
          <svg
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refrescar
        </button>
      </div>

      {/* Loading / Error */}
      {loading && !ordenes && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm text-center py-12 text-slate-400">
          <svg
            className="animate-spin w-6 h-6 mx-auto mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Cargando órdenes de compra…
        </div>
      )}

      {error && (
        <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center text-red-600 text-sm">
          Error al cargar datos: {error}
        </div>
      )}

      {/* Calendar grid */}
      {ordenes && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-3 px-2">
            {MONTHS[month]} {year}
          </h3>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1.5 text-center text-xs mb-1.5">
            {DAY_HEADERS.map((day) => (
              <div key={day} className="font-medium text-slate-500 py-1.5">
                {day}
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1.5">
              {week.map((day, di) => {
                if (day === null) {
                  return <div key={di} className="min-h-[90px]" />;
                }

                const dateStr = formatDateKey(year, month, day);
                const ocsForDay = byDate[dateStr] || [];
                const isToday = isCurrentMonth && day === todayDay;

                return (
                  <div
                    key={di}
                    className={`rounded-lg p-1.5 min-h-[90px] border ${
                      isToday
                        ? 'bg-brand-50 border-brand-300 ring-1 ring-brand-200'
                        : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <span
                      className={`text-xs font-medium pl-0.5 ${
                        isToday
                          ? 'text-brand-700 bg-brand-100 rounded-full px-1.5 py-0.5'
                          : 'text-slate-400'
                      }`}
                    >
                      {day}
                    </span>
                    <div className="space-y-0.5 mt-0.5">
                      {ocsForDay.map(renderOCCard)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Empty state */}
          {pendientes.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              No hay órdenes de compra pendientes para este mes
            </div>
          )}
        </div>
      )}

      {/* "Sin fecha" section */}
      {sinFecha.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-3">
            Sin fecha de entrega ({sinFecha.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sinFecha.map(renderOCCard)}
          </div>
        </div>
      )}
    </div>
  );
}
